import { supabase } from '../config/supabase';

export const fileService = {
  // Upload file to Supabase Storage and save metadata
  async uploadFile(file, projectId, folderId = null) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Validate file size (50MB limit)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      throw new Error('File size exceeds 50MB limit');
    }

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png', 
      'image/gif',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/vnd.oasis.opendocument.spreadsheet',
      'message/rfc822',
      'application/vnd.ms-outlook',
      'text/plain'
    ];

    if (!allowedTypes.includes(file.type)) {
      throw new Error(`File type ${file.type} is not supported`);
    }

    // Generate unique filename with original extension
    const fileExt = file.name.split('.').pop().toLowerCase();
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2);
    const fileName = `${timestamp}_${randomId}.${fileExt}`;
    
    // Simpler path structure
    const filePath = `${projectId}/${folderId || 'root'}/${fileName}`;

    // Upload to Supabase Storage with retry logic
    let uploadData, uploadError;
    let retries = 3;
    
    while (retries > 0) {
      const result = await supabase.storage
        .from('project-files')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      uploadData = result.data;
      uploadError = result.error;
      
      if (!uploadError) break;
      
      retries--;
      if (retries > 0) {
        console.log(`Upload failed, retrying... (${retries} attempts left)`);
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
      }
    }

    if (uploadError) {
      console.error('Upload error:', uploadError);
      throw new Error(`Failed to upload file: ${uploadError.message}`);
    }

    // Save file metadata to database
    const { data: fileData, error: dbError } = await supabase
      .from('files')
      .insert([{
        filename: fileName,
        original_name: file.name,
        file_type: file.type,
        file_path: uploadData.path,
        file_size: file.size,
        project_id: projectId,
        folder_id: folderId,
        uploaded_by: user.id
      }])
      .select()
      .single();

    if (dbError) throw dbError;
    return fileData;
  },

  // Delete file from storage and database
  async deleteFile(fileId) {
    // Get file info first
    const { data: fileData, error: fetchError } = await supabase
      .from('files')
      .select('file_path')
      .eq('id', fileId)
      .single();

    if (fetchError) throw fetchError;

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('project-files')
      .remove([fileData.file_path]);

    if (storageError) throw storageError;

    // Delete from database
    const { error: dbError } = await supabase
      .from('files')
      .delete()
      .eq('id', fileId);

    if (dbError) throw dbError;
    return true;
  },

  // Get download URL for file
  async getFileUrl(filePath) {
    // For public bucket, use public URL (faster and no expiry)
    const { data } = supabase.storage
      .from('project-files')
      .getPublicUrl(filePath);

    return data.publicUrl;
  },

  // Get files for project
  async getProjectFiles(projectId, folderId = null) {
    let query = supabase
      .from('files')
      .select('*')
      .eq('project_id', projectId);

    if (folderId) {
      query = query.eq('folder_id', folderId);
    } else {
      query = query.is('folder_id', null);
    }

    const { data, error } = await query.order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  }
};

export default fileService;
